import React, { useMemo, useState } from 'react'
import { UpdatePrice } from '../../../assets/interfaces/priceInterface'
import './customTable.css'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useIsProjectEditor } from '../../../hooks/useUserPermissions'

function CustomTable(props) {
    const [hoveredCell, setHoveredCell] = useState(null)
    const [copiedCell, setCopiedCell] = useState(null)
    const [tooltipPos, setTooltipPos] = useState(null)
    const isProjectEditor = useIsProjectEditor()
    const tooltipStyle = tooltipPos ? { position: 'fixed', bottom: tooltipPos.bottom, right: tooltipPos.right } : null

    function onInfoMouseEnter(cellKey, e) {
        setHoveredCell(cellKey)
        const rect = e.currentTarget.getBoundingClientRect()
        setTooltipPos({
            bottom: window.innerHeight - rect.top + 8,
            right: window.innerWidth - rect.right,
        })
    }

    function onInfoMouseLeave() {
        setHoveredCell(null)
        setTooltipPos(null)
    }
    // Recalculate options whenever props.Prices changes
    const Options = useMemo(() => {
        if (!props.Prices || props.Prices.length === 0) {
            return []
        }

        // Separate base unit prices (no option) and option prices
        const baseUnitPrices = []
        const optionsMap = new Map()

        props.Prices.forEach((price) => {
            if (!price.optionId) {
                // Base unit price (no option)
                baseUnitPrices.push(price)
            } else if (price.option) {
                // Option price
                if (!optionsMap.has(price.optionId)) {
                    optionsMap.set(price.optionId, {
                        id: price.optionId,
                        number: price.option.number || '',
                        name: price.option.name || '',
                        prices: [],
                    })
                }
                optionsMap.get(price.optionId).prices.push(price)
            }
        })

        // Sort base unit prices by size
        baseUnitPrices.sort((a, b) => {
            const numA = parseFloat(a.size)
            const numB = parseFloat(b.size)
            if (!isNaN(numA) && !isNaN(numB)) {
                return numA - numB
            }
            return a.size.localeCompare(b.size, undefined, { numeric: true })
        })

        // Convert options map to array and sort by option number
        const sortedOptions = Array.from(optionsMap.values())

        // Sort prices by size within each option
        sortedOptions.map((option) => {
            option.prices.sort((a, b) => {
                const numA = parseFloat(a.size)
                const numB = parseFloat(b.size)
                if (!isNaN(numA) && !isNaN(numB)) {
                    return numA - numB
                }
                return a.size.localeCompare(b.size, undefined, { numeric: true })
            })
        })

        // Create base unit "option" if there are base unit prices
        const result = []
        if (baseUnitPrices.length > 0) {
            result.push({
                id: null,
                number: '',
                name: '',
                prices: baseUnitPrices,
            })
        }

        // Add all other options
        result.push(...sortedOptions)

        return result
    }, [props.Prices])

    function onPriceChange(Price, event) {
        if (!isProjectEditor) {
            return
        }

        let value = event.target.value.trim()
        // Allow empty string, numbers, minus sign, and decimal point
        if (!isNaN(value) || value === '-' || value === '.' || value === '') {
            // Find the price in the original props.Prices array
            const updatedPrices = props.Prices.map((p) => {
                if (p.id === Price.id) {
                    return {
                        ...p,
                        oldPrice: p.oldPrice !== undefined ? p.oldPrice : p.price,
                        price: value === '' ? null : value,
                    }
                }
                return p
            })

            // Notify parent component
            if (props.onPricesUpdate) {
                props.onPricesUpdate(updatedPrices)
            }
        }
    }

    function onInputFocusOut(Price) {
        if (!isProjectEditor) {
            return
        } else if (Price.oldPrice !== undefined && Price.oldPrice !== Price.price) {
            UpdatePrice(Price)
        }
    }

    function symbolFromWord(code) {
        if (!code) return null
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: code.toLowerCase(),
            currencyDisplay: 'narrowSymbol',
        })
            .formatToParts(1)
            .find((p) => p.type === 'currency').value
    }

    // Determine the status and styling for a price cell
    function getPriceStatus(inputPrice) {
        const hasKmat = inputPrice.kmat && inputPrice.kmat.trim() !== ''
        const hasVarcond = inputPrice.varcond && inputPrice.varcond.trim() !== ''
        const hasReference = inputPrice.reference && inputPrice.reference.trim() !== ''

        // Complete: has all required data (kmat + varcond) OR has reference
        if (((hasKmat && hasVarcond) || hasReference) && inputPrice.price) {
            return {
                status: 'complete',
                icon: <InfoOutlinedIcon color='info' />,
                label: 'Complete',
            }
        }
        // Incomplete: missing some required data
        return {
            status: 'incomplete',
            icon: <InfoOutlinedIcon color='inherit' className='infoIcon' />,
            label: 'Incomplete',
        }
    }

    // Build detailed info for tooltip
    function buildDetailedInfo(price) {
        const hasKmat = price.kmat && price.kmat.trim() !== ''
        const hasVarcond = price.varcond && price.varcond.trim() !== ''
        const hasReference = price.reference && price.reference.trim() !== ''
        const hasQuantity = price.quantity && price.quantity.trim() !== ''

        return {
            kmat: hasKmat ? price.kmat : null,
            varcond: hasVarcond ? price.varcond : null,
            reference: hasReference ? price.reference : null,
            quantity: hasQuantity ? price.quantity : null,
        }
    }

    // Copy data to clipboard
    function copyToClipboard(price, event) {
        event.stopPropagation()

        const info = buildDetailedInfo(price)
        const textToCopy = [
            info.kmat ? `KMAT: ${info.kmat}` : 'KMAT: Missing',
            info.varcond ? `Varcond: ${info.varcond}` : info.kmat ? 'Varcond: Missing' : null,
            info.reference ? `Reference: ${info.reference}` : null,
            info.quantity ? `Quantity: ${info.quantity}` : null,
        ]
            .filter(Boolean)
            .join('\n')

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                const cellKey = `${price.optionId || 'base'}-${price.id}`
                setCopiedCell(cellKey)
                setTimeout(() => setCopiedCell(null), 1500)
            })
            .catch((err) => {
                console.error('Failed to copy:', err)
            })
    }

    if (props.loading) {
        return <div className='PriceTableContainer'>Loading...</div>
    }

    if (!Options || Options.length === 0 || !Options[0] || !Options[0].prices) {
        return <div className='PriceTableContainer'>No price data available</div>
    }

    return (
        <div className='PriceTableContainer'>
            <table className='PricesTable' style={{ width: Options[0].prices.length * 100 + 'px' }}>
                <tbody>
                    <tr>
                        <th></th>
                        {Options[0].prices.map((price) => (
                            <th key={price.id} className='TableHeader'>
                                {price.size}
                            </th>
                        ))}
                    </tr>
                    {Options.map((Opt) => (
                        <tr key={Opt.id || 'base-unit'}>
                            <td className='TableRowHeader'>
                                <span className='OptNumberCellSpan'>{Opt.number}</span>
                                <span className='OptNameCellSpan'>{Opt.name}</span>
                            </td>
                            {Opt.prices.map((price) => {
                                const priceStatus = getPriceStatus(price)
                                const info = buildDetailedInfo(price)
                                const cellKey = `${Opt.id || 'base'}-${price.id}`
                                const isHovered = hoveredCell === cellKey
                                const isCopied = copiedCell === cellKey

                                return price.compatible ? (
                                    <td
                                        key={price.id}
                                        className={
                                            'PriceCell ' +
                                            (price.price !== null && price.price !== undefined && price.price !== ''
                                                ? ''
                                                : 'EmptyPriceCell')
                                        }>
                                        <div className='PriceCellContent'>
                                            <input
                                                disabled={!isProjectEditor}
                                                onChange={(event) => onPriceChange(price, event)}
                                                onBlur={() => onInputFocusOut(price)}
                                                className='PriceCellInput'
                                                value={
                                                    price.price !== null && price.price !== undefined ? price.price : ''
                                                }
                                            />
                                            <span className='PriceCellCurrency'>{symbolFromWord(props.currency)}</span>

                                            <div
                                                className='InfoButtonWrapper'
                                                onMouseEnter={(e) => onInfoMouseEnter(cellKey, e)}
                                                onMouseLeave={onInfoMouseLeave}>
                                                <button
                                                    className='InfoButton'
                                                    type='button'
                                                    onClick={(e) => copyToClipboard(price, e)}>
                                                    {isCopied ? '⧉' : priceStatus.icon}
                                                </button>

                                                {isHovered && !isCopied && tooltipPos && (
                                                    <div className='InfoTooltip' style={tooltipStyle}>
                                                        <div className='TooltipTitle'>{priceStatus.label}</div>
                                                        <div className='TooltipDivider'></div>
                                                        <div className='TooltipRow'>
                                                            <span className='TooltipLabel'>KMAT:</span>
                                                            <span
                                                                className={
                                                                    info.kmat ? 'TooltipValue' : 'TooltipValueMissing'
                                                                }>
                                                                {info.kmat || 'Missing'}
                                                            </span>
                                                        </div>
                                                        {(info.kmat || info.varcond) && (
                                                            <div className='TooltipRow'>
                                                                <span className='TooltipLabel'>Varcond:</span>
                                                                <span
                                                                    className={
                                                                        info.varcond
                                                                            ? 'TooltipValue'
                                                                            : 'TooltipValueMissing'
                                                                    }>
                                                                    {info.varcond || 'Missing'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {info.reference && (
                                                            <div className='TooltipRow'>
                                                                <span className='TooltipLabel'>Reference:</span>
                                                                <span className='TooltipValue'>{info.reference}</span>
                                                            </div>
                                                        )}
                                                        {info.quantity && (
                                                            <div className='TooltipRow'>
                                                                <span className='TooltipLabel'>Quantity:</span>
                                                                <span className='TooltipValue'>{info.quantity}</span>
                                                            </div>
                                                        )}
                                                        <div className='TooltipFooter'>Click to copy</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                ) : (
                                    <td key={price.id} className='NotCompatiblePriceCell'>
                                        NC
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default CustomTable
